import type { CSSProperties, ReactNode } from "react";
import MuiTypography from "@mui/material/Typography";
import Link from "@mui/material/Link";

// antd <Typography> + Text/Title/Paragraph/Link -> MUI Typography/Link.
export interface TextProps {
  children?: ReactNode;
  type?: "secondary" | "success" | "warning" | "danger";
  strong?: boolean;
  italic?: boolean;
  disabled?: boolean;
  ellipsis?: boolean;
  code?: boolean;
  style?: CSSProperties;
  className?: string;
  onClick?: () => void;
  "data-cy"?: string;
  "data-testid"?: string;
}

const COLOR: Record<string, string> = {
  secondary: "text.secondary", success: "success.main", warning: "warning.main", danger: "error.main",
};

const Text = ({ children, type, strong, italic, disabled, ellipsis, code, style, className, onClick, "data-cy": dataCy, "data-testid": dataTestid }: TextProps) => (
  <MuiTypography
    component="span"
    variant="body2"
    color={type ? COLOR[type] : undefined}
    onClick={onClick}
    data-cy={dataCy}
    data-testid={dataTestid}
    sx={{
      fontWeight: strong ? 600 : undefined,
      fontStyle: italic ? "italic" : undefined,
      opacity: disabled ? 0.4 : undefined,
      // overflow/text-overflow are no-ops on an inline element; display:block (+ a width
      // bound) makes truncation actually apply so long text ellipsizes in narrow panels.
      ...(ellipsis ? { display: "block", minWidth: 0, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } : {}),
      ...(code ? { fontFamily: "monospace", bgcolor: "action.hover", px: 0.5, borderRadius: 0.5 } : {}),
    }}
    style={style}
    className={className}
  >
    {children}
  </MuiTypography>
);

interface TitleProps {
  children?: ReactNode;
  level?: 1 | 2 | 3 | 4 | 5;
  style?: CSSProperties;
  className?: string;
}
const Title = ({ children, level = 1, style, className }: TitleProps) => (
  <MuiTypography variant={(`h${level}` as "h1")} style={style} className={className}>
    {children}
  </MuiTypography>
);

const Paragraph = ({ children, style, className }: TextProps) => (
  <MuiTypography variant="body2" paragraph style={style} className={className}>
    {children}
  </MuiTypography>
);

interface LinkProps { children?: ReactNode; href?: string; target?: string; onClick?: () => void; style?: CSSProperties; className?: string }
const TLink = ({ children, href, target, onClick, style, className }: LinkProps) => (
  <Link href={href} target={target} onClick={onClick} underline="hover" style={style} className={className}>
    {children}
  </Link>
);

export const Typography = ({ children, style, className }: { children?: ReactNode; style?: CSSProperties; className?: string }) => (
  <div style={style} className={className}>{children}</div>
);

Typography.Text = Text;
Typography.Title = Title;
Typography.Paragraph = Paragraph;
Typography.Link = TLink;

export default Typography;
